// Formal Quote Module (CLAUDE.md) — list of past quotes.
import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import quoteService from '../services/quoteService';
import ConfirmModal from '../components/ConfirmModal';

export default function QuotesListPage() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      setQuotes(await quoteService.getQuotes());
    } catch {
      setError('Failed to load quotes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async () => {
    try {
      await quoteService.deleteQuote(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete quote.');
      setDeleteTarget(null);
    }
  };

  // Hundreds of quotes a year in, client-side filtering over the already-loaded list is plenty —
  // no need for a server-side search endpoint at this scale. Matches by customer/prospect name,
  // quote #, or date, since those are the things someone would actually remember a year later.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return quotes;
    return quotes.filter(quote =>
      (quote.customerName || '').toLowerCase().includes(q) ||
      String(quote.id).includes(q) ||
      (quote.quoteDate || '').includes(q)
    );
  }, [quotes, search]);

  return (
    <div className="container mt-4 pb-5">
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-start align-items-md-center mb-4 pb-3 gap-2" style={{ borderBottom: '2px solid #f1f3f5' }}>
        <h4 className="mb-0">📄 Quotes</h4>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/quotes/new')}>+ New Quote</button>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {!loading && quotes.length > 0 && (
        <div className="mb-3">
          <input
            className="form-control form-control-sm"
            style={{ maxWidth: 340 }}
            placeholder="🔍 Search by customer, quote #, or date…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      )}

      {loading ? (
        <div className="text-center text-muted py-5">
          <div className="spinner-border spinner-border-sm me-2" />Loading…
        </div>
      ) : quotes.length === 0 ? (
        <div className="text-center text-muted py-5 border rounded">
          No quotes yet. <Link to="/quotes/new">Create one →</Link>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-muted py-5 border rounded">
          No quotes match "{search}".
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Customer / Prospect</th>
                <th>Date</th>
                <th>Valid Until</th>
                <th className="text-end">Items</th>
                <th className="text-end">Total</th>
                <th>Status</th>
                <th style={{ width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(q => (
                <tr key={q.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/quotes/${q.id}`)}>
                  <td className="text-muted">{q.id}</td>
                  <td className="fw-semibold">{q.customerName || '—'}</td>
                  <td>{q.quoteDate}</td>
                  <td>{q.validUntil}</td>
                  <td className="text-end">{q.itemCount}</td>
                  <td className="text-end">${Number(q.total).toFixed(2)}</td>
                  <td>
                    {q.pushedToPriceListAt
                      ? <span className="badge bg-success">Pushed to Price List</span>
                      : <span className="badge bg-secondary">Draft</span>}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => setDeleteTarget(q)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        show={!!deleteTarget}
        title="Delete Quote"
        message={deleteTarget?.pushedToPriceListAt
          ? `Delete the quote for "${deleteTarget?.customerName}"? This only removes this quote's own record — the prices it already pushed will stay on the customer's real price list. This cannot be undone.`
          : `Delete the quote for "${deleteTarget?.customerName}"? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
